import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface UseTodoSectionAccessReturn {
  /** principalIds granted access to each section (direct grants only — the
   *  subsection cascade is applied by the caller against the section tree). */
  accessBySection: Map<string, string[]>;
  loading: boolean;
  /** Replace one section's access set (owner/admin only). */
  setSectionAccess: (sectionId: string, principalIds: string[]) => Promise<void>;
}

/**
 * Per-section "who can work here" grants for a shared list. A member with access
 * to a section may manage the tasks inside it and all of its subsections; this
 * hook exposes the raw direct grants (the cascade is computed against the section
 * tree by consumers). Mutations are owner/admin-only server-side.
 */
export function useTodoSectionAccess(
  holderId: string | null,
  listId: string,
): UseTodoSectionAccessReturn {
  const queryClient = useQueryClient();
  const key = queryKeys.todoLists.sectionAccess(holderId ?? '', listId);
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.listTodoSectionAccess(holderId as string, listId),
    enabled: !!holderId && !!listId,
  });

  const accessBySection = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const grant of data?.access ?? []) {
      const arr = map.get(grant.sectionId) ?? [];
      arr.push(grant.principalId);
      map.set(grant.sectionId, arr);
    }
    return map;
  }, [data]);

  const setMut = useMutation({
    mutationFn: ({ sectionId, principalIds }: { sectionId: string; principalIds: string[] }) =>
      api.setTodoSectionAccess(holderId as string, sectionId, principalIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: () => {
      toast({ title: 'Could not update access', variant: 'destructive' });
    },
  });

  return {
    accessBySection,
    loading: isLoading,
    setSectionAccess: async (sectionId, principalIds) => {
      await setMut.mutateAsync({ sectionId, principalIds });
    },
  };
}
